"use client";

import { useState } from "react";
import { trackEvent } from "@/lib/posthog";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "general",
          message: message.trim(),
          page_url: window.location.pathname,
        }),
      });
      trackEvent("feedback_submitted", {
        type: "general",
        message: message.trim(),
        page_url: window.location.pathname,
      });
      setSubmitted(true);
      setTimeout(() => {
        setSubmitted(false);
        setMessage("");
        onClose();
      }, 1500);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setMessage("");
    setSubmitted(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[80] bg-black/40 transition-opacity" onClick={handleClose} />

      {/* Modal */}
      <div className="animate-slide-up fixed inset-x-0 bottom-0 z-[90] mx-auto w-full max-w-lg">
        <div className="bg-background rounded-t-2xl px-6 pt-6 pb-10 shadow-xl">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="text-on-surface-variant hover:text-on-surface absolute top-4 right-4 transition-colors"
            aria-label="Fermer"
          >
            <span className="material-symbols-outlined">close</span>
          </button>

          {submitted ? (
            <div className="flex flex-col items-center gap-4 py-8">
              <span
                className="material-symbols-outlined text-5xl text-green-600"
                style={{ fontVariationSettings: "'FILL' 1" }}
              >
                check_circle
              </span>
              <p className="text-on-surface text-xl font-bold">Merci pour votre retour !</p>
            </div>
          ) : (
            <>
              <h2 className="text-on-surface mb-6 text-2xl font-bold">Donnez votre avis</h2>

              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Qu'est-ce qui pourrait être amélioré ?"
                rows={4}
                className="bg-surface-container-low border-outline-variant/30 text-on-surface placeholder:text-outline focus:ring-primary/40 mb-4 w-full resize-none rounded-xl border px-4 py-3 text-sm focus:ring-2 focus:outline-none"
              />

              <button
                onClick={handleSubmit}
                disabled={submitting || !message.trim()}
                className="btn-primary w-full py-4 disabled:opacity-50"
              >
                {submitting ? "Envoi..." : "Envoyer"}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
